import {
  GlobalActivationMediumFilterDTO,
  GlobalActivationSubtypeFilterDTO,
  GlobalActivationSubTouchpointFilterDTO,
  GlobalActivationAssetFilterDTO,
  GlobalActivationItemFilterDTO,
  GlobalActivationTypeFilterDTO,
  GlobalBrandFilterDTO,
  GlobalCityFilterDTO,
  GlobalConsumerSegmentFilterDTO,
  GlobalDqsFilterDTO,
  GlobalGenderFilterDTO,
  GlobalYobFilterDTO,
  GlobalContetualizationFilterDTO,
  GlobalContetualizationBenchmarkFilterDTO,
  GlobalContetualizationDateFilterDTO,
  GlobalCountryFilterDTO,
  GlobalCurrencyFilterDTO,
  GlobalDateFilterDTO,
  GlobalFilterTypes,
  GlobalPropertyFilterDTO,
  GlobalRegionFilterDTO,
  GlobalSearchTypesDTO,
} from '@/@dtos/GlobalSearchDTO';
import { ApiProvider } from '@/provider/ApiProvider';
import {
  getHeaderFilters,
  setHeaderBrandSelection,
  setHeaderPropertySelection,
} from '@/selection/headerSelection';
import { useHeaderSelection } from '@/selection/useHeaderSelection';
import { endOfYear, format, startOfYear } from 'date-fns';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type FormDataRenameFilters = {
  from?: string;
  to?: string;
  currency?: string;
  property?: string;
  brand?: string;
  country?: string;
  activationType?: string;
  activationSubtype?: string;
  activationSubTouchpoint?: string;
  activationAsset?: string;
  activationItem?: string;
  medium?: string;
  consumerSegment?: string;
  dqs?: string;
  gender?: string;
  yob?: string;
  contetualization?: string;
  contetualizationBenchmark?: string;
  contetualizationStartDate?: string;
  contetualizationEndDate?: string;
  region?: string;
  city?: string;
};

type MakeFormDataConfigs = {
  required?: GlobalFilterTypes[];
  rename?: FormDataRenameFilters;
};

type HeaderEntity = { id: string | number; [key: string]: unknown };

// eslint-disable-next-line no-unused-vars
type HeaderChangeGuard = () => boolean | Promise<boolean>;

type GlobalFilters = {
  from: string;
  to: string;
  currency: GlobalCurrencyFilterDTO | null;
  country: GlobalCountryFilterDTO[];
  brand: GlobalBrandFilterDTO[];
  property: GlobalPropertyFilterDTO[];
  activationType: GlobalActivationTypeFilterDTO[];
  activationSubtype: GlobalActivationSubtypeFilterDTO[];
  activationSubTouchpoint: GlobalActivationSubTouchpointFilterDTO[];
  activationAsset: GlobalActivationAssetFilterDTO[];
  activationItem: GlobalActivationItemFilterDTO[];
  activationMedium: GlobalActivationMediumFilterDTO[];
  consumerSegment: GlobalConsumerSegmentFilterDTO[];
  dqs: GlobalDqsFilterDTO[];
  gender: GlobalGenderFilterDTO[];
  yob: GlobalYobFilterDTO[];
  contetualization: GlobalContetualizationFilterDTO[];
  contetualizationBenchmark: string;
  contetualizationStartDate: string;
  contetualizationEndDate: string;
  region: GlobalRegionFilterDTO[];
  city: GlobalCityFilterDTO[];
  headerBrandId: string | null;
  headerPropertyId: string | null;
};

type GlobalFiltersContextProps = {
  globalFilters: GlobalFilters;
  /**
   * True while a single-brand page (ROO, advocacy: plan §13.4) is open: the
   * multi-select Brand filter is hidden and never sent; the page sends its one
   * brand itself.
   */
  isSingleBrandPage: boolean;
  // eslint-disable-next-line no-unused-vars
  registerSingleBrandPage: () => () => void;
  showCityRegion: () => void;
  hideCityRegion: () => void;
  isCityRegionEnabled: boolean;
  // eslint-disable-next-line no-unused-vars
  makeFormDataFromGlobalFilters: (config: MakeFormDataConfigs) => FormData;
  // eslint-disable-next-line no-unused-vars
  updateGlobalFilters: (filters: GlobalSearchTypesDTO[]) => void;
  setHeaderBrand: (
    // eslint-disable-next-line no-unused-vars
    brand: HeaderEntity | null,
    // eslint-disable-next-line no-unused-vars
    options?: { skipGuard?: boolean },
  ) => Promise<boolean>;
  // eslint-disable-next-line no-unused-vars
  setHeaderProperty: (
    // eslint-disable-next-line no-unused-vars
    property: HeaderEntity | null,
    // eslint-disable-next-line no-unused-vars
    options?: { skipGuard?: boolean },
  ) => Promise<boolean>;
  // eslint-disable-next-line no-unused-vars
  registerHeaderChangeGuard: (guard: HeaderChangeGuard | null) => void;
};

type GlobalFiltersProviderProps = {
  children: ReactNode;
};

const GlobalFiltersContext = createContext<GlobalFiltersContextProps>(
  {} as GlobalFiltersContextProps,
);

/**
 * The global filters of the pages: the APPLIED filter values (dates,
 * currency, countries, ...; set by the header's "Apply") and the header
 * brand / partner.
 *
 * The header selection itself is not kept here: it lives in memory under the
 * active role assignment (selection/headerSelection.ts, UI state plan step
 * A; nothing of it is in browser storage) and this provider follows it, so
 * `headerBrandId` / `headerPropertyId` are always the selection's.
 */
export function GlobalFiltersProvider({ children }: GlobalFiltersProviderProps) {
  const headerSelection = useHeaderSelection();
  const [appliedFilters, setGlobalFilters] = useState<GlobalFilters>({
    from: '',
    to: '',
    currency: null,
    country: [],
    brand: [],
    property: [],
    activationType: [],
    activationSubtype: [],
    activationSubTouchpoint: [],
    activationAsset: [],
    activationItem: [],
    activationMedium: [],
    consumerSegment: [],
    dqs: [],
    gender: [],
    yob: [],
    contetualization: [],
    contetualizationBenchmark: '',
    contetualizationStartDate: '',
    contetualizationEndDate: '',
    region: [],
    city: [],
    headerBrandId: null,
    headerPropertyId: null,
  });
  // Single-brand pages open (a counter: nested registrations). The ref is read
  // when a request is built, the state re-renders the filter bar.
  const singleBrandPagesRef = useRef(0);
  const [isSingleBrandPage, setIsSingleBrandPage] = useState(false);
  const registerSingleBrandPage = useCallback(function (): () => void {
    singleBrandPagesRef.current += 1;
    setIsSingleBrandPage(true);
    return () => {
      singleBrandPagesRef.current = Math.max(0, singleBrandPagesRef.current - 1);
      setIsSingleBrandPage(singleBrandPagesRef.current > 0);
    };
  }, []);
  const [enableCityRegion, setEnableCityRegion] = useState(false);

  const globalFilters = useMemo<GlobalFilters>(
    () => ({
      ...appliedFilters,
      headerBrandId: headerSelection.brandId,
      headerPropertyId: headerSelection.propertyId,
    }),
    [appliedFilters, headerSelection.brandId, headerSelection.propertyId],
  );

  const headerChangeGuardRef = useRef<HeaderChangeGuard | null>(null);

  const registerHeaderChangeGuard = useCallback(function (guard: HeaderChangeGuard | null): void {
    headerChangeGuardRef.current = guard;
  }, []);

  const runHeaderChangeGuard = useCallback(async function (): Promise<boolean> {
    const guard = headerChangeGuardRef.current;
    if (!guard) return true;
    try {
      return await guard();
    } catch {
      return false;
    }
  }, []);

  const makeFormDataFromGlobalFilters = useCallback(
    function ({ required = [], rename = {} }: MakeFormDataConfigs) {
      const requiredKeys = required;
      const renamingObject = rename;

      const formData = new FormData();
      const {
        to,
        from,
        country,
        brand,
        property,
        currency,
        activationType,
        activationSubtype,
        activationSubTouchpoint,
        activationAsset,
        activationItem,
        activationMedium,
        consumerSegment,
        dqs,
        gender,
        yob,
        contetualization,
        contetualizationBenchmark,
        contetualizationStartDate,
        contetualizationEndDate,
        region,
        city,
      } = globalFilters;

      const formNames = {
        from: renamingObject.from ?? 'from_date',
        to: renamingObject.to ?? 'to_date',
        currency: renamingObject.currency ?? 'currency_id',
        country: renamingObject.country ?? 'country_id',
        brand: renamingObject.brand ?? 'brand_id',
        property: renamingObject.property ?? 'property_id',
        activationType: renamingObject.activationType ?? 'activation_type_id',
        activationSubtype: renamingObject.activationSubtype ?? 'activation_subtype_id',
        activationSubTouchpoint: renamingObject.activationSubTouchpoint ?? 'sub_touchpoint',
        activationAsset: renamingObject.activationAsset ?? 'asset_id',
        activationItem: renamingObject.activationItem ?? 'item_id',
        activationMedium: renamingObject.medium ?? 'medium_id',
        consumerSegment: renamingObject.consumerSegment ?? 'consumer_segment_id',
        dqs: renamingObject.dqs ?? 'dqs_id',
        gender: renamingObject.gender ?? 'gender',
        yob: renamingObject.yob ?? 'yob',
        contetualization: renamingObject.contetualization ?? 'contextualization_filter',
        contetualizationBenchmark: renamingObject.contetualizationBenchmark ?? 'benchmark_group_id',
        contetualizationStartDate: renamingObject.contetualizationStartDate ?? 'context_start_date',
        contetualizationEndDate: renamingObject.contetualizationEndDate ?? 'context_end_date',
        region: renamingObject.region ?? 'region_id',
        city: renamingObject.city ?? 'city_id',
      };

      if (requiredKeys.includes('dates')) {
        if (!!to && !!from) {
          formData.append(formNames.from, from);
          formData.append(formNames.to, to);
        } else {
          const firstDayOfYear = format(startOfYear(new Date()), 'M/d/yyyy');
          const lastDayOfYear = format(endOfYear(new Date()), 'M/d/yyyy');

          formData.append(formNames.from, firstDayOfYear);
          formData.append(formNames.to, lastDayOfYear);
        }
      }

      if (requiredKeys.includes('currency') && !!currency) {
        formData.append(formNames.currency, currency.id.toString());
      }

      if (!currency) {
        const userDefaultCurrencyId = ApiProvider.returnUserData()?.currency_id ?? 52;
        formData.append(formNames.currency, userDefaultCurrencyId);
      }

      if (requiredKeys.includes('property') && property.length) {
        property.forEach((element) => {
          formData.append(`${formNames.property}[]`, element.id.toString());
        });
      }

      // Never on a single-brand page: there the page sends its one brand (§13.4).
      if (requiredKeys.includes('brand') && brand.length && singleBrandPagesRef.current === 0) {
        brand.forEach((element) => {
          formData.append(`${formNames.brand}[]`, element.id.toString());
        });
      }

      if (requiredKeys.includes('country') && country.length) {
        country.forEach((element) => {
          formData.append(`${formNames.country}[]`, element.id.toString());
        });
      }

      if (requiredKeys.includes('region') && region.length) {
        region.forEach((element) => {
          formData.append(`${formNames.region}[]`, element.id.toString());
        });
      }

      if (requiredKeys.includes('city') && city.length) {
        city.forEach((element) => {
          formData.append(`${formNames.city}[]`, element.id.toString());
        });
      }

      if (requiredKeys.includes('activation_type') && activationType.length) {
        activationType.forEach((value) => {
          formData.append(`${formNames.activationType}[]`, value.id.toString());
        });
      }

      if (requiredKeys.includes('activation_subtype') && activationSubtype.length) {
        activationSubtype.forEach((value) => {
          formData.append(`${formNames.activationSubtype}[]`, value.id.toString());
        });
      }

      if (requiredKeys.includes('activationSubTouchpoint') && activationSubTouchpoint.length) {
        activationSubTouchpoint.forEach((value) => {
          formData.append(`${formNames.activationSubTouchpoint}[]`, value.id.toString());
        });
      }

      if (requiredKeys.includes('activationAsset') && activationAsset.length) {
        activationAsset.forEach((value) => {
          formData.append(`${formNames.activationAsset}[]`, value.id.toString());
        });
      }

      if (requiredKeys.includes('activationItem') && activationItem.length) {
        activationItem.forEach((value) => {
          formData.append(`${formNames.activationItem}[]`, value.id.toString());
        });
      }

      if (requiredKeys.includes('medium') && activationMedium.length) {
        activationMedium.forEach((value) => {
          formData.append(`${formNames.activationMedium}[]`, value.id.toString());
        });
      }

      if (requiredKeys.includes('consumer_segment') && consumerSegment.length) {
        consumerSegment.forEach((value) => {
          formData.append(`${formNames.consumerSegment}[]`, value.id.toString());
        });
      }

      if (requiredKeys.includes('dqs') && dqs.length) {
        dqs.forEach((value) => {
          formData.append(`${formNames.dqs}[]`, value.id.toString());
        });
      }

      if (requiredKeys.includes('gender') && gender.length) {
        gender.forEach((value) => {
          formData.append(`${formNames.gender}`, value.id.toString());
        });
      }

      if (requiredKeys.includes('yob') && yob.length) {
        yob.forEach((value) => {
          formData.append(`min_${formNames.yob}`, value.min.toString());
          formData.append(`max_${formNames.yob}`, value.max.toString());
        });
      }

      if (requiredKeys.includes('contetualization') && contetualization.length) {
        contetualization.forEach((value) => {
          formData.append(`${formNames.contetualization}`, value.value.toString());
          if (value.value.toString() == 'date_range') {
            formData.append(`${formNames.contetualizationStartDate}`, contetualizationStartDate);
            formData.append(`${formNames.contetualizationEndDate}`, contetualizationEndDate);
          }
          if (value.value.toString() == 'benchmark') {
            formData.append(`${formNames.contetualizationBenchmark}`, contetualizationBenchmark);
          }
        });
      }

      return formData;
    },
    [globalFilters],
  );

  const updateGlobalFilters = useCallback(function (newFilters: GlobalSearchTypesDTO[]): void {
    setGlobalFilters(convertGlobalArrayToFilter(newFilters));
  }, []);

  const setHeaderBrand = useCallback(
    async function (
      brand: HeaderEntity | null,
      options: { skipGuard?: boolean } = {},
    ): Promise<boolean> {
      // skipGuard: an automatic selection (the scope's only brand), not a user switch.
      const proceed = options.skipGuard ? true : await runHeaderChangeGuard();
      if (!proceed) return false;
      // null and the "Remove Filter" entry (id `clear`) clear the header brand
      setHeaderBrandSelection(brand);
      return true;
    },
    [runHeaderChangeGuard],
  );

  const setHeaderProperty = useCallback(
    async function (
      property: HeaderEntity | null,
      options: { skipGuard?: boolean } = {},
    ): Promise<boolean> {
      // skipGuard: an automatic selection (the scope's only partner), not a user switch.
      const proceed = options.skipGuard ? true : await runHeaderChangeGuard();
      if (!proceed) return false;
      if (property && property.id !== 'clear') {
        setHeaderPropertySelection(property);
      } else {
        // Clearing the header property also clears the linked brand,
        // matching PropertySelect's pre-existing "Remove Filter" behavior.
        setHeaderPropertySelection(null);
        setHeaderBrandSelection(null);
      }
      return true;
    },
    [runHeaderChangeGuard],
  );

  const hideCityRegion = useCallback(function () {
    setEnableCityRegion(false);
  }, []);

  const showCityRegion = useCallback(function () {
    setEnableCityRegion(true);
  }, []);

  useEffect(() => {
    // the selection the page load put into memory (the server's last one, or the defaults)
    const globalSearch = (getHeaderFilters() ?? []) as unknown as GlobalSearchTypesDTO[];

    setGlobalFilters(convertGlobalArrayToFilter(globalSearch));
  }, []);

  const contextValue = useMemo(
    () => ({
      showCityRegion,
      hideCityRegion,
      isCityRegionEnabled: enableCityRegion,
      globalFilters,
      isSingleBrandPage,
      registerSingleBrandPage,
      makeFormDataFromGlobalFilters,
      updateGlobalFilters,
      setHeaderBrand,
      setHeaderProperty,
      registerHeaderChangeGuard,
    }),
    [
      showCityRegion,
      hideCityRegion,
      enableCityRegion,
      globalFilters,
      isSingleBrandPage,
      registerSingleBrandPage,
      makeFormDataFromGlobalFilters,
      updateGlobalFilters,
      setHeaderBrand,
      setHeaderProperty,
      registerHeaderChangeGuard,
    ],
  );

  return (
    <GlobalFiltersContext.Provider value={contextValue}>{children}</GlobalFiltersContext.Provider>
  );
}

function convertGlobalArrayToFilter(arrayOfFilters: GlobalSearchTypesDTO[] | '') {
  let fromFilter = '';
  let toFilter = '';
  let currencyFilter = null;
  const countryFilter: GlobalCountryFilterDTO[] = [];
  const regionFilter: GlobalRegionFilterDTO[] = [];
  const cityFilter: GlobalCityFilterDTO[] = [];
  const brandFilter: GlobalBrandFilterDTO[] = [];
  const propertyFilter: GlobalPropertyFilterDTO[] = [];
  const activationTypeFilter: GlobalActivationTypeFilterDTO[] = [];
  const activationSubtypeFilter: GlobalActivationSubtypeFilterDTO[] = [];
  const activationSubTouchpointFilter: GlobalActivationSubTouchpointFilterDTO[] = [];
  const activationAssetFilter: GlobalActivationAssetFilterDTO[] = [];
  const activationItemFilter: GlobalActivationItemFilterDTO[] = [];
  const activationMediumFilter: GlobalActivationMediumFilterDTO[] = [];
  const consumerSegmentFilter: GlobalConsumerSegmentFilterDTO[] = [];
  const dqsFilter: GlobalDqsFilterDTO[] = [];
  const genderFilter: GlobalGenderFilterDTO[] = [];
  const yobFilter: GlobalYobFilterDTO[] = [];
  const contetualizationFilter: GlobalContetualizationFilterDTO[] = [];
  let contetualizationBenchmarkFilter = '';
  let contetualizationStartDateFilter = '';
  let contetualizationEndDateFilter = '';

  if (arrayOfFilters !== '') {
    arrayOfFilters.forEach((item) => {
      switch (item.filter_type) {
        case 'from':
          fromFilter = (item as GlobalDateFilterDTO).name;
          break;
        case 'to':
          toFilter = (item as GlobalDateFilterDTO).name;
          break;
        case 'currency':
          currencyFilter = item as GlobalCurrencyFilterDTO;
          break;
        case 'country':
          countryFilter.push(item as GlobalCountryFilterDTO);
          break;
        case 'activationType':
          activationTypeFilter.push(item as GlobalActivationTypeFilterDTO);
          break;
        case 'activationSubtype':
          activationSubtypeFilter.push(item as GlobalActivationSubtypeFilterDTO);
          break;
        case 'activationSubTouchpoint':
          activationSubTouchpointFilter.push(item as GlobalActivationSubTouchpointFilterDTO);
          break;
        case 'activationAsset':
          activationAssetFilter.push(item as GlobalActivationAssetFilterDTO);
          break;
        case 'activationItem':
          activationItemFilter.push(item as GlobalActivationItemFilterDTO);
          break;
        case 'activationMedium':
          activationMediumFilter.push(item as GlobalActivationMediumFilterDTO);
          break;
        case 'activationSegment':
          consumerSegmentFilter.push(item as GlobalConsumerSegmentFilterDTO);
          break;
        case 'dqs':
          dqsFilter.push(item as GlobalDqsFilterDTO);
          break;
        case 'gender':
          genderFilter.push(item as GlobalGenderFilterDTO);
          break;
        case 'yob':
          yobFilter.push(item as GlobalYobFilterDTO);
          break;
        case 'contetualization':
          contetualizationFilter.push(item as GlobalContetualizationFilterDTO);
          break;
        case 'contetualizationBenchMark':
          contetualizationBenchmarkFilter = (item as GlobalContetualizationBenchmarkFilterDTO).id;
          break;
        case 'contetualizationFromDate':
          contetualizationStartDateFilter = (item as GlobalContetualizationDateFilterDTO).name;
          break;
        case 'contetualizationToDate':
          contetualizationEndDateFilter = (item as GlobalContetualizationDateFilterDTO).name;
          break;
        case 'brand':
          brandFilter.push(item as GlobalBrandFilterDTO);
          break;
        case 'property':
          propertyFilter.push(item as GlobalPropertyFilterDTO);
          break;
        case 'region':
          regionFilter.push(item as GlobalRegionFilterDTO);
          break;
        case 'city':
          cityFilter.push(item as GlobalCityFilterDTO);
          break;
        default:
          return;
      }
    });
  }

  if (!currencyFilter) {
    currencyFilter = ApiProvider.returnUserData()?.currency ?? null;
  }

  return {
    from: fromFilter,
    to: toFilter,
    currency: currencyFilter,
    country: countryFilter,
    brand: brandFilter,
    property: propertyFilter,
    activationType: activationTypeFilter,
    activationSubtype: activationSubtypeFilter,
    activationSubTouchpoint: activationSubTouchpointFilter,
    activationAsset: activationAssetFilter,
    activationItem: activationItemFilter,
    activationMedium: activationMediumFilter,
    consumerSegment: consumerSegmentFilter,
    dqs: dqsFilter,
    gender: genderFilter,
    yob: yobFilter,
    contetualization: contetualizationFilter,
    contetualizationBenchmark: contetualizationBenchmarkFilter,
    contetualizationStartDate: contetualizationStartDateFilter,
    contetualizationEndDate: contetualizationEndDateFilter,
    region: regionFilter,
    city: cityFilter,
    headerBrandId: null,
    headerPropertyId: null,
  };
}

export function useGlobalFilters() {
  const context = useContext(GlobalFiltersContext);

  return context;
}

/**
 * Marks the calling page as a single-brand page (ROO, advocacy; plan §13.4)
 * while it is mounted. Registered in a layout effect, so it is in place before
 * the page's graphs build their first request (their effects run after).
 */
export function useSingleBrandPage(enabled = true): void {
  const { registerSingleBrandPage } = useContext(GlobalFiltersContext);
  useLayoutEffect(() => {
    if (!enabled || !registerSingleBrandPage) return undefined;
    return registerSingleBrandPage();
  }, [enabled, registerSingleBrandPage]);
}
